import { Box, Paper } from "@mui/material"
import { DownloadOutlined, FilterAltOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { useState } from "react"

import { useApAgingReportList } from "../hooks/useApAgingReportList"
import { useExportApAgingReport } from "../hooks/useExportApAgingReport"
import { toApAgingInvoiceSort } from "../utils"

import { ApAgingSummary } from "./ApAgingSummary"
import { ApAgingVendorTable } from "./ApAgingVendorTable"
import { ApAgingFilterChips } from "./filters/ApAgingFilterChips"
import { ApAgingFilterDrawer } from "./filters/ApAgingFilterDrawer"

import { Button } from "@/components/common/Button"
import SearchInput from "@/components/SearchInput/SearchInput"

export const ApAgingReportTabPanel: FC = () => {
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  // The search input is uncontrolled and reads its text only when it mounts, so Clear All remounts it. Until the cleared
  // search has reached the URL the new input is given no text, or it would bring the old text back.
  const [searchInputKey, setSearchInputKey] = useState(0)
  const [isClearPending, setIsClearPending] = useState(false)

  const {
    vendors,
    summary,
    isFetching,
    error,
    searchParams,
    invoiceSorting,
    expanded,
    tableContainerRef,
    handleScroll,
    handleExpandedChange,
    handleInvoiceSortingChange,
    handleSearchChange,
    handleFilterChange,
    handleFiltersChange,
    handleFilterRemove,
    handleFilterClear,
  } = useApAgingReportList()
  const { exportApAgingReport, isExporting } = useExportApAgingReport()

  // Adjusted during render, not in an effect.
  if (isClearPending && !searchParams.query) setIsClearPending(false)

  const handleClearAll = () => {
    setSearchInputKey((key) => key + 1)
    setIsClearPending(true)
    handleFilterClear()
  }

  return (
    <Box className='flex size-full flex-col'>
      <Paper elevation={0} className='flex min-h-0 grow flex-col gap-6 overflow-hidden bg-white'>
        <ApAgingSummary summary={summary} />

        <Box className='flex items-center gap-x-4'>
          <SearchInput
            key={searchInputKey}
            placeholder='Search by Vendor or Vendor #...'
            defaultValue={isClearPending ? undefined : searchParams.query}
            onChange={(e) => handleSearchChange(e.target.value)}
            className='w-full max-w-[524px]'
            data-testid='ap-aging-report.tab-panel.search-input'
          />
          <Button
            appearance='outlined'
            startIcon={<FilterAltOutlined />}
            onClick={() => setIsFilterOpen(true)}
            rootElementName='button'
            data-testid='ap-aging-report.tab-panel.filter-button'
          >
            Filter
          </Button>
          {/* AK-6964 AC10: The Excel export reflects the filters currently applied. */}
          <Button
            appearance='outlined'
            startIcon={<DownloadOutlined />}
            disabled={isExporting}
            onClick={() => void exportApAgingReport(searchParams, toApAgingInvoiceSort(invoiceSorting))}
            rootElementName='button'
            data-testid='ap-aging-report.tab-panel.download-button'
          >
            Download (Excel)
          </Button>
        </Box>

        <ApAgingFilterChips searchParams={searchParams} onRemove={handleFilterRemove} />

        <Box className='flex min-h-0 flex-1 flex-col'>
          <ApAgingVendorTable
            data={vendors}
            invoiceSorting={invoiceSorting}
            onInvoiceSortingChange={handleInvoiceSortingChange}
            expanded={expanded}
            onExpandedChange={handleExpandedChange}
            loading={isFetching}
            error={error}
            containerRef={tableContainerRef}
            onScroll={handleScroll}
          />
        </Box>

        <ApAgingFilterDrawer
          open={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          searchParams={searchParams}
          onChange={handleFilterChange}
          onBatchChange={handleFiltersChange}
          onClear={handleClearAll}
        />
      </Paper>
    </Box>
  )
}
